import {
  myChatTokenUsageSchema,
  myTokenUsageAggregateSchema,
} from "../schema/chat-token-usage";
import type {
  IGetMyTokenUsageAggregateParams,
  TMyChatTokenUsageResponse,
  TMyTokenUsageAggregateResponse,
} from "../types/chat-token-usage";
import type {
  IExportTokenUsageBreakdownParams,
  IExportTokenUsageByModelParams,
  IGetTokenUsageBreakdownParams,
  IGetTokenUsageBreakdownResponse,
  IGetTokenUsageByModelParams,
  IGetTokenUsageByModelResponse,
  IGetTokenUsageParams,
  IGetTokenUsageResponse,
  IGetTokenUsageSummaryParams,
  IGetTokenUsageSummaryResponse,
  IGetTokenUsageTrendsParams,
  IGetTokenUsageTrendsResponse,
} from "../types/token-usage";
import type {
  IGetUserAnalyticsBaseParams,
  IGetUserAnalyticsParams,
  IGetUserAnalyticsResponse,
  IGetUserAnalyticsSettingsParams,
  IGetUserAnalyticsSpikingAgentsResponse,
  IGetUserAnalyticsTrendsParams,
  IGetUserAnalyticsTrendsResponse,
  IUpdateUserAnalyticsSettingsParams,
  IUserAnalyticsSettings,
} from "../types/token-usage-user-analytics";
import type {
  IGetUserInventoryDetailsParams,
  IGetUserInventoryDetailsResponse,
  IGetUserInventoryParams,
  IGetUserInventoryResponse,
} from "../types/token-usage-user-inventory";
import type { ApiClient } from "./client";

export class TokenUsageApi {
  constructor(private apiClient: ApiClient) {}

  async getTokenUsage(
    params: IGetTokenUsageParams,
  ): Promise<IGetTokenUsageResponse> {
    const response = await this.apiClient.get<IGetTokenUsageResponse>(
      "/token-usage",
      { params },
    );
    return response.data;
  }

  async getTokenUsageSummary(
    params: IGetTokenUsageSummaryParams,
  ): Promise<IGetTokenUsageSummaryResponse> {
    const response = await this.apiClient.get<IGetTokenUsageSummaryResponse>(
      "/token-usage/summary",
      { params },
    );
    return response.data;
  }

  async getTokenUsageByModel(
    params: IGetTokenUsageByModelParams,
  ): Promise<IGetTokenUsageByModelResponse> {
    const response = await this.apiClient.get<IGetTokenUsageByModelResponse>(
      "/token-usage/by-model",
      { params },
    );
    return response.data;
  }

  async getTokenUsageTrends(
    params: IGetTokenUsageTrendsParams,
  ): Promise<IGetTokenUsageTrendsResponse> {
    const response = await this.apiClient.get<IGetTokenUsageTrendsResponse>(
      "/token-usage/by-model/trends",
      { params },
    );
    return response.data;
  }

  async exportTokenUsageByModel(
    params: IExportTokenUsageByModelParams,
  ): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      "/token-usage/by-model/export",
      { params, responseType: "blob" },
    );
    return response.data;
  }

  // NOTE: NSAI-7024 — tokens and cost per assistant, agent or source.
  async getTokenUsageBreakdown(
    params: IGetTokenUsageBreakdownParams,
  ): Promise<IGetTokenUsageBreakdownResponse> {
    const response = await this.apiClient.get<IGetTokenUsageBreakdownResponse>(
      "/token-usage/breakdown",
      { params },
    );
    return response.data;
  }

  // NOTE: NSAI-5717 — the open chat's own usage, for the person holding it.
  async getMyChatTokenUsage({
    chatUuid,
  }: {
    chatUuid: string;
  }): Promise<TMyChatTokenUsageResponse> {
    const response = await this.apiClient.get<unknown>(
      `/token-usage/me/conversation/${chatUuid}`,
    );
    return myChatTokenUsageSchema.parse(response.data);
  }

  async getMyTokenUsageAggregate(
    params: IGetMyTokenUsageAggregateParams,
  ): Promise<TMyTokenUsageAggregateResponse> {
    const response = await this.apiClient.get<unknown>(
      "/token-usage/me/aggregate",
      { params },
    );
    return myTokenUsageAggregateSchema.parse(response.data);
  }

  async exportTokenUsageBreakdown(
    params: IExportTokenUsageBreakdownParams,
  ): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      "/token-usage/breakdown/export",
      { params, responseType: "blob" },
    );
    return response.data;
  }

  // NOTE: NSAI-8010 — per-user ranking, trends and spikes for an organization
  // owner.
  async getUserAnalytics(
    params: IGetUserAnalyticsParams,
  ): Promise<IGetUserAnalyticsResponse> {
    const response = await this.apiClient.get<IGetUserAnalyticsResponse>(
      "/token-usage/user-analytics",
      { params },
    );
    return response.data;
  }

  async getUserAnalyticsTrends(
    params: IGetUserAnalyticsTrendsParams,
  ): Promise<IGetUserAnalyticsTrendsResponse> {
    const response = await this.apiClient.get<IGetUserAnalyticsTrendsResponse>(
      "/token-usage/user-analytics/trends",
      { params },
    );
    return response.data;
  }

  async getUserAnalyticsSpikingAgents(
    params: IGetUserAnalyticsBaseParams,
  ): Promise<IGetUserAnalyticsSpikingAgentsResponse> {
    const response =
      await this.apiClient.get<IGetUserAnalyticsSpikingAgentsResponse>(
        "/token-usage/user-analytics/spiking-agents",
        { params },
      );
    return response.data;
  }

  async getUserAnalyticsSettings(
    params: IGetUserAnalyticsSettingsParams,
  ): Promise<IUserAnalyticsSettings> {
    const response = await this.apiClient.get<IUserAnalyticsSettings>(
      "/token-usage/user-analytics/settings",
      { params },
    );
    return response.data;
  }

  async updateUserAnalyticsSettings(
    params: IUpdateUserAnalyticsSettingsParams,
  ): Promise<IUserAnalyticsSettings> {
    const response = await this.apiClient.post<IUserAnalyticsSettings>(
      "/token-usage/user-analytics/settings/update",
      params,
    );
    return response.data;
  }

  // NOTE: NSAI-8010 — who builds and uses what, per user, for an organization
  // owner.
  async getUserInventory(
    params: IGetUserInventoryParams,
  ): Promise<IGetUserInventoryResponse> {
    const response = await this.apiClient.get<IGetUserInventoryResponse>(
      "/token-usage/user-analytics/inventory",
      { params },
    );
    return response.data;
  }

  async getUserInventoryDetails({
    userUuid,
    ...params
  }: IGetUserInventoryDetailsParams): Promise<IGetUserInventoryDetailsResponse> {
    const response = await this.apiClient.get<IGetUserInventoryDetailsResponse>(
      `/token-usage/user-analytics/inventory/${encodeURIComponent(userUuid)}/details`,
      { params },
    );
    return response.data;
  }
}
